<template>
    <div class="resume-requests">
        <search-resume-requests @user-selected="handleUserSelected"></search-resume-requests>

        <div class="resume-requests-grid">
            <div v-if="isLoading" class="rr-lds-grid">
                <p>Loading resume requests...</p>
            </div>

            <div class="resume-requests-list" v-else-if="resumeRequests.length">
                <div
                    class="resume-request-card"
                    v-for="request in resumeRequests"
                    :key="request.id"
                    @click="openModal(request)"
                >
                    <div class="rr-card-header">
                        <h3 class="rr-product-name">{{ request.product_name || 'N/A' }}</h3>
                        <span class="rr-status-badge" :class="statusClass(request.status)">
                            {{ request.status || 'Pending' }}
                        </span>
                    </div>
                    <div class="rr-card-body">
                        <div class="rr-info-item">
                            <label>Patient:</label>
                            <a :href="'/dashboard/patients/' + request.patient_id" @click.stop>
                                {{ patientName(request) }}
                            </a>
                        </div>
                        <div class="rr-info-item">
                            <label>Created:</label>
                            <span>{{ localTime(request.created_at) }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="rr-no-requests">
                {{ emptyMessage }}
            </div>

            <div class="resume-requests-actions">
                <div class="resume-requests-actions-left">
                    <button
                        type="button"
                        class="rr-custom-action-button"
                        v-if="totalPages > 1 && !isSearchActive"
                        @click="goLastPage"
                    >
                        Last Page
                    </button>
                    <div class="rr-status-filter">
                        <label for="rr-status-filter">Status:</label>
                        <select
                            id="rr-status-filter"
                            v-model="selectedStatus"
                            class="rr-status-select"
                            @change="handleStatusChange"
                        >
                            <option
                                v-for="option in statusOptions"
                                :key="option.value"
                                :value="option.value"
                            >
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div v-if="errorMessage" class="rr-error-message">{{ errorMessage }}</div>
                </div>
                <div class="resume-requests-actions-right" v-if="!isSearchActive">
                    <div class="rr-pagination-controls">
                        <span v-if="page > 0" class="rr-click-effect" @click="prevPage">
                            <i class="fas fa-chevron-left"></i>Prev
                        </span>
                        <span
                            v-if="totalItems > perPage && page < totalPages - 1"
                            class="rr-click-effect"
                            @click="nextPage"
                        >
                            Next<i class="fas fa-chevron-right"></i>
                        </span>
                    </div>
                    <div class="rr-pagination-info" v-if="!isSearchActive">
                        <p>Current Page: {{ totalPages ? page + 1 : 0 }}</p>
                        <p>Pages: {{ totalPages }}</p>
                        <p>Items: {{ totalItems }}</p>
                    </div>
                </div>
            </div>
        </div>

        <resume-request-detail-modal
            :show="showModal"
            :resume-request="selectedRequest"
            :all-requests="resumeRequests"
            :current-page="page"
            :total-pages="totalPages"
            @close="closeModal"
            @previous="previousRequest"
            @next="nextRequest"
            @load-page="loadPage"
            @updated="handleRequestUpdated"
        ></resume-request-detail-modal>
    </div>
</template>

<script>
import axios from 'axios';
import SearchResumeRequests from './SearchResumeRequests.vue';
import ResumeRequestDetailModal from './ResumeRequestDetailModal.vue';
import { apiMessage, localTime, patientName, statusClass } from './resumeRequestHelpers';

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'all', label: 'All Statuses' },
];

const DEFAULT_STATUS = 'pending';

export default {
    name: 'ResumeRequests',

    components: {
        SearchResumeRequests,
        ResumeRequestDetailModal,
    },

    data() {
        return {
            resumeRequests: [],
            page: 0,
            perPage: 10,
            totalPages: 0,
            totalItems: 0,
            isLoading: false,
            isSearchActive: false,
            statusOptions: STATUS_OPTIONS,
            selectedStatus: DEFAULT_STATUS,
            errorMessage: '',
            showModal: false,
            selectedRequest: null,
        };
    },

    computed: {
        emptyMessage() {
            if (this.isSearchActive) {
                return 'There are no resume requests for this patient.';
            }

            if (this.selectedStatus === 'all') {
                return 'There are no resume requests.';
            }

            const option = this.statusOptions.find((item) => item.value === this.selectedStatus);

            return `There are no ${option ? option.label.toLowerCase() : this.selectedStatus} resume requests.`;
        },
    },

    mounted() {
        this.load();
    },

    methods: {
        patientName,
        statusClass,
        localTime,

        queryParams() {
            const params = { page: this.page + 1, per_page: this.perPage };

            if (this.selectedStatus !== 'all') {
                params.status = this.selectedStatus;
            }

            return params;
        },

        load() {
            this.isLoading = true;
            this.isSearchActive = false;
            this.errorMessage = '';

            axios
                .get('/api/v1/resume-requests', { params: this.queryParams() })
                .then((response) => {
                    this.applyResponse(response);

                    if (this.outOfRange()) {
                        this.page = Math.max(0, this.totalPages - 1);
                        this.load();
                        return;
                    }

                    this.isLoading = false;
                })
                .catch((error) => {
                    this.resumeRequests = [];
                    this.totalPages = 0;
                    this.totalItems = 0;
                    this.errorMessage = apiMessage(error, 'An error occurred while fetching resume requests');
                    this.isLoading = false;
                });
        },

        outOfRange() {
            return !this.resumeRequests.length && this.totalItems > 0 && this.page > this.totalPages - 1;
        },

        applyResponse(response) {
            const payload = response && response.data;
            const items = payload && payload.data;

            this.resumeRequests = Array.isArray(items) ? items : [];
            this.perPage = (payload && payload.per_page) || this.perPage;
            this.totalItems = (payload && payload.total) || 0;
            this.totalPages = this.totalItems ? Math.ceil(this.totalItems / this.perPage) : 0;

            if (this.showModal && this.resumeRequests.length) {
                this.selectedRequest = this.resumeRequests[0];
            }
        },

        handleStatusChange() {
            this.page = 0;
            this.errorMessage = '';
            this.closeModal();
            this.load();
        },

        handleUserSelected(matches) {
            if (!matches || !matches.length) {
                this.page = 0;
                this.load();
                return;
            }

            this.isSearchActive = true;
            this.resumeRequests = matches;
            this.totalItems = matches.length;
            this.totalPages = 1;
            this.page = 0;
        },

        openModal(request) {
            this.selectedRequest = request;
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
            this.selectedRequest = null;
        },

        currentIndex() {
            if (!this.selectedRequest) {
                return -1;
            }

            return this.resumeRequests.findIndex((request) => request.id === this.selectedRequest.id);
        },

        previousRequest() {
            const index = this.currentIndex();

            if (index > 0) {
                this.selectedRequest = this.resumeRequests[index - 1];
            }
        },

        nextRequest() {
            const index = this.currentIndex();

            if (index >= 0 && index < this.resumeRequests.length - 1) {
                this.selectedRequest = this.resumeRequests[index + 1];
            }
        },

        handleRequestUpdated() {
            this.load();
        },

        nextPage() {
            this.page += 1;
            this.load();
        },

        prevPage() {
            this.page -= 1;
            this.load();
        },

        goLastPage() {
            if (this.totalPages > 0) {
                this.page = this.totalPages - 1;
                this.load();
            }
        },

        loadPage(page) {
            this.page = page;
            this.load();
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/ResumeRequests.vue